/**
 * ML Data Sources Tab Component
 * Manages external SDOH data sources and Trust.Care integration for ML model training
 *
 * Trust Marketplace - ML Prediction System
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { getWeaveinApiUrl } from '@/src/config';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import {
  Database,
  Globe,
  RefreshCw,
  Play,
  CheckCircle2,
  AlertCircle,
  XCircle,
  Download,
  Upload,
  Settings,
  Brain,
  BarChart3,
  MapPin,
  Users,
  Activity,
  ExternalLink,
  Clock,
  TrendingUp,
  Shield,
  FileText,
  Sparkles,
  Building2,
  Car,
  Cloud,
  Home,
  Thermometer,
  Droplets,
  Flame,
  Wind,
  DollarSign,
  Receipt,
  Calculator,
  Search,
  Layers
} from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { authFetch } from '@/lib/authFetch';

interface MLDataSource {
  id: number;
  source_name: string;
  source_type: string;
  source_category: string;
  source_url: string;
  geography_level: string;
  data_domain: string;
  refresh_frequency: string;
  last_updated_date: string;
  is_active: boolean;
  is_validated: boolean;
  data_quality_score: number;
  record_count: number;
  coverage_percentage: number;
  description: string;
  license_type: string;
  attribution_required: boolean;
}

interface SyncJob {
  id: number;
  job_id: string;
  source_id: number;
  source_system: string;
  data_type: string;
  sync_mode: string;
  status: string;
  started_at: string;
  completed_at: string;
  records_extracted: number;
  records_loaded: number;
  records_failed: number;
  data_quality_score: number;
}

interface MLStats {
  total_sources: number;
  active_sources: number;
  total_sdoh_records: number;
  total_training_members: number;
  total_features_computed: number;
  last_feature_refresh: string;
  model_count: number;
  active_model: string;
  total_predictions?: number;
}

interface Organization {
  id: number;
  name: string;
  type: string;
}

interface PredictionResult {
  success: boolean;
  message: string;
  predictions_created: number;
  risk_distribution?: {
    critical: number;
    high: number;
    moderate: number;
    low: number;
  };
}

interface ExternalSDOHSource {
  id: string;
  name: string;
  type: 'transportation' | 'climate' | 'housing';
  api_provider: string;
  description: string;
  data_fields: string[];
  last_sync?: string;
  record_count: number;
  is_active: boolean;
  api_status: 'connected' | 'pending' | 'error';
}

interface ZipCodeSDOHData {
  zip_code: string;
  transportation?: {
    walk_score?: number;
    transit_score?: number;
    bike_score?: number;
    transportation_access_score?: number;
    transportation_barrier_score?: number;
  };
  climate?: {
    climate_risk_score?: number;
    aqi_annual_median?: number;
    flood_risk_level?: string;
    wildfire_risk_level?: string;
    environmental_health_score?: number;
  };
  housing?: {
    eviction_rate?: number;
    pct_cost_burdened?: number;
    housing_stability_score?: number;
    housing_affordability_score?: number;
    housing_risk_score?: number;
  };
  combined_sdoh_risk_score?: number;
}

const MLDataSourcesTab: React.FC = () => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Data
  const [sources, setSources] = useState<MLDataSource[]>([]);
  const [syncJobs, setSyncJobs] = useState<SyncJob[]>([]);
  const [stats, setStats] = useState<MLStats | null>(null);

  // Modals
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [showTrustCareModal, setShowTrustCareModal] = useState(false);
  const [showPredictionModal, setShowPredictionModal] = useState(false);
  const [selectedSource, setSelectedSource] = useState<MLDataSource | null>(null);

  // Sync status
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState(0);

  // Prediction generation
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);
  const [predictionHorizon, setPredictionHorizon] = useState<number>(14);
  const [generatingPredictions, setGeneratingPredictions] = useState(false);
  const [predictionResult, setPredictionResult] = useState<PredictionResult | null>(null);

  // Tab selection
  const [activeTab, setActiveTab] = useState<'ml-sources' | 'external-sdoh' | 'org-ml-privacy' | 'pricing-data'>('ml-sources');

  // Pricing Data Sources state
  interface PricingSourceConfig {
    id: string;
    name: string;
    source_type: 'cms_mpfs' | 'medicaid' | 'commercial' | 'internal';
    description: string;
    status: 'connected' | 'syncing' | 'error' | 'pending';
    last_sync?: string;
    record_count: number;
    codes_covered: number;
    states_covered: string[];
    data_quality_score: number;
  }
  interface PricingStats {
    total_codes: number;
    medicare_codes: number;
    medicaid_codes: number;
    states_covered: number;
    last_full_refresh: string;
    ml_model_version: string;
    ml_model_accuracy: number;
  }
  const [pricingSources, setPricingSources] = useState<PricingSourceConfig[]>(getDefaultPricingSources());
  const [pricingStats, setPricingStats] = useState<PricingStats>(getDefaultPricingStats());
  const [syncingPricingSource, setSyncingPricingSource] = useState<string | null>(null);
  const [pricingCodeLookup, setPricingCodeLookup] = useState<string>('');
  const [pricingLookupResult, setPricingLookupResult] = useState<any>(null);
  const [lookingUpPricing, setLookingUpPricing] = useState(false);
  const [refreshingPricing, setRefreshingPricing] = useState(false);

  // Organization ML Privacy Settings
  interface OrgMLConfig {
    id: number;
    org_id: number;
    org_name: string;
    org_type?: string;
    pii_ml_enabled: boolean;
    deidentified_ml_enabled: boolean;
    ml_data_sharing_tier: 'none' | 'org_only' | 'deidentified_cross_org';
    last_training_date?: string;
    model_version?: string;
    member_count: number;
    features_count: number;
    prediction_count: number;
    status: 'Active' | 'Limited' | 'Inactive';
    consent_given_at?: string;
    created_at?: string;
    updated_at?: string;
  }
  const [orgMLConfigs, setOrgMLConfigs] = useState<OrgMLConfig[]>([]);
  const [loadingOrgConfigs, setLoadingOrgConfigs] = useState(false);
  const [selectedOrgConfig, setSelectedOrgConfig] = useState<OrgMLConfig | null>(null);
  const [savingOrgConfig, setSavingOrgConfig] = useState<number | null>(null);
  const [orgConfigError, setOrgConfigError] = useState<string | null>(null);

  // External SDOH Sources
  const [externalSources, setExternalSources] = useState<ExternalSDOHSource[]>(getExternalSDOHSources());
  const [syncingExternal, setSyncingExternal] = useState<string | null>(null);
  const [externalSyncProgress, setExternalSyncProgress] = useState(0);
  const [zipCodeLookup, setZipCodeLookup] = useState<string>('');
  const [zipCodeData, setZipCodeData] = useState<ZipCodeSDOHData | null>(null);
  const [lookingUpZip, setLookingUpZip] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  // Fetch org ML configs when switching to that tab
  useEffect(() => {
    if (activeTab === 'org-ml-privacy') {
      fetchOrgMLConfigs();
    }
  }, [activeTab]);

  // Fetch organization ML privacy configurations
  const fetchOrgMLConfigs = async () => {
    try {
      setLoadingOrgConfigs(true);
      setOrgConfigError(null);

      const response = await authFetch(getWeaveinApiUrl('/api/ml/org-privacy'));

      if (response.ok) {
        const data = await response.json();
        setOrgMLConfigs(data.configs || []);
      } else {
        console.error('Failed to fetch org ML configs');
        setOrgConfigError('Failed to load organization ML privacy configurations');
      }
    } catch (err) {
      console.error('Error fetching org ML configs:', err);
      setOrgConfigError('Error loading organization ML privacy configurations');
    } finally {
      setLoadingOrgConfigs(false);
    }
  };

  // Update organization ML privacy settings
  const updateOrgMLConfig = async (
    orgId: number,
    updates: {
      pii_ml_enabled?: boolean;
      deidentified_ml_enabled?: boolean;
      ml_data_sharing_tier?: 'none' | 'org_only' | 'deidentified_cross_org';
    }
  ) => {
    try {
      setSavingOrgConfig(orgId);
      setOrgConfigError(null);


      const response = await authFetch(getWeaveinApiUrl(`/api/ml/org-privacy/${orgId}`), {
        method: 'PUT',
        jsonBody: updates
      });

      if (response.ok) {
        const updatedConfig = await response.json();
        // Update the local state with the new config
        setOrgMLConfigs(prev =>
          prev.map(config =>
            config.org_id === orgId ? { ...config, ...updatedConfig } : config
          )
        );
      } else {
        const errorData = await response.json();
        setOrgConfigError(errorData.detail || 'Failed to update ML privacy configuration');
      }
    } catch (err) {
      console.error('Error updating org ML config:', err);
      setOrgConfigError('Error saving ML privacy configuration');
    } finally {
      setSavingOrgConfig(null);
    }
  };

  // Toggle PII ML enabled
  const handleTogglePiiML = async (orgId: number, currentValue: boolean) => {
    await updateOrgMLConfig(orgId, {
      pii_ml_enabled: !currentValue,
      ml_data_sharing_tier: !currentValue ? 'org_only' : 'none'
    });
  };

  // Toggle Cross-Org ML enabled
  const handleToggleCrossOrgML = async (orgId: number, currentValue: boolean) => {
    await updateOrgMLConfig(orgId, {
      deidentified_ml_enabled: !currentValue,
      ml_data_sharing_tier: !currentValue ? 'deidentified_cross_org' : 'org_only'
    });
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch ML data sources and stats
      const [sourcesRes, statsRes, jobsRes] = await Promise.all([
        authFetch(getWeaveinApiUrl('/api/ml/data-sources')),
        authFetch(getWeaveinApiUrl('/api/ml/stats')),
        authFetch(getWeaveinApiUrl('/api/ml/sync-jobs?limit=20'))
      ]);

      // Handle mock data if endpoints not yet implemented
      if (sourcesRes.ok) {
        setSources(await sourcesRes.json());
      } else {
        // Use mock data for now
        setSources(getMockSources());
      }

      if (statsRes.ok) {
        setStats(await statsRes.json());
      } else {
        setStats(getMockStats());
      }

      if (jobsRes.ok) {
        setSyncJobs(await jobsRes.json());
      } else {
        setSyncJobs([]);
      }

    } catch (err) {
      console.error('Error fetching ML data:', err);
      // Use mock data on error
      setSources(getMockSources());
      setStats(getMockStats());
    } finally {
      setLoading(false);
    }
  };

  const handleSyncSource = async (source: MLDataSource) => {
    setSelectedSource(source);
    setSyncing(true);
    setSyncProgress(0);

    try {

      // Start sync job
      const response = await authFetch(getWeaveinApiUrl(`/api/ml/data-sources/${source.id}/sync`), {
        method: 'POST'
      });

      if (response.ok) {
        // Poll for progress
        const interval = setInterval(async () => {
          setSyncProgress(prev => {
            if (prev >= 100) {
              clearInterval(interval);
              setSyncing(false);
              fetchData();
              return 100;
            }
            return prev + 10;
          });
        }, 500);
      } else {
        throw new Error('Sync failed');
      }
    } catch (err) {
      console.error('Sync error:', err);
      setSyncing(false);
      // Simulate progress for demo
      simulateSync();
    }
  };

  const simulateSync = () => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.random() * 15;
      setSyncProgress(Math.min(100, progress));
      if (progress >= 100) {
        clearInterval(interval);
        setTimeout(() => {
          setSyncing(false);
          fetchData();
        }, 500);
      }
    }, 300);
  };

  const fetchOrganizations = async () => {
    try {
      const response = await authFetch(getWeaveinApiUrl('/api/cbos'));

      if (response.ok) {
        const data = await response.json();
        // Map the response to include type field for display
        const orgsWithType = data.map((org: { id: number; name: string; is_sponsor?: boolean }) => ({
          id: org.id,
          name: org.name,
          type: org.is_sponsor ? 'sponsor' : 'cbo'
        }));
        setOrganizations(orgsWithType);
        if (orgsWithType.length > 0 && !selectedOrgId) {
          setSelectedOrgId(orgsWithType[0].id);
        }
      }
    } catch (err) {
      console.error('Error fetching organizations:', err);
    }
  };

  const handleOpenPredictionModal = () => {
    setPredictionResult(null);
    fetchOrganizations();
    setShowPredictionModal(true);
  };

  const handleGeneratePredictions = async () => {
    if (!selectedOrgId) return;

    setGeneratingPredictions(true);
    setPredictionResult(null);

    try {
      const response = await authFetch(getWeaveinApiUrl(`/api/ml/predictions/generate/${selectedOrgId}?horizon=${predictionHorizon}`), {
        method: 'POST'
      });

      const data = await response.json();

      if (response.ok) {
        setPredictionResult({
          success: true,
          message: data.message || 'Predictions generated successfully',
          predictions_created: data.predictions_generated || data.predictions_created || 0,
          risk_distribution: data.risk_distribution
        });
        // Refresh stats to show updated prediction count
        fetchData();
      } else {
        setPredictionResult({
          success: false,
          message: data.detail || 'Failed to generate predictions',
          predictions_created: 0
        });
      }
    } catch (err) {
      console.error('Error generating predictions:', err);
      setPredictionResult({
        success: false,
        message: 'An error occurred while generating predictions',
        predictions_created: 0
      });
    } finally {
      setGeneratingPredictions(false);
    }
  };

  // Fetch ZIP code SDOH data from new endpoints
  const handleLookupZipCode = async () => {
    if (!zipCodeLookup || zipCodeLookup.length < 5) return;

    setLookingUpZip(true);
    setZipCodeData(null);

    try {
      const response = await authFetch(
        getWeaveinApiUrl(`/api/sdoh-data/zip/${zipCodeLookup}/comprehensive`)
      );

      if (response.ok) {
        const data = await response.json();
        setZipCodeData(data);
      } else {
        console.error('Failed to fetch ZIP code data');
        // Set placeholder data to show structure
        setZipCodeData({
          zip_code: zipCodeLookup,
          transportation: { walk_score: null, transit_score: null, bike_score: null },
          climate: { climate_risk_score: null, flood_risk_level: 'unknown', wildfire_risk_level: 'unknown' },
          housing: { eviction_rate: null, pct_cost_burdened: null, housing_stability_score: null },
          combined_sdoh_risk_score: null
        });
      }
    } catch (err) {
      console.error('Error looking up ZIP code:', err);
    } finally {
      setLookingUpZip(false);
    }
  };

  // Sync external SDOH source
  const handleSyncExternalSource = async (sourceId: string) => {
    setSyncingExternal(sourceId);
    setExternalSyncProgress(0);

    try {

      // Attempt to call real API endpoint (which may return pending status)
      // For now simulate the sync since actual API calls require external services
      let progress = 0;
      const interval = setInterval(() => {
        progress += Math.random() * 20;
        setExternalSyncProgress(Math.min(100, progress));
        if (progress >= 100) {
          clearInterval(interval);
          setTimeout(() => {
            setSyncingExternal(null);
            // Update the source with new sync timestamp
            setExternalSources(prev => prev.map(s =>
              s.id === sourceId
                ? { ...s, last_sync: new Date().toISOString(), record_count: s.record_count + Math.floor(Math.random() * 100) }
                : s
            ));
          }, 300);
        }
      }, 400);
    } catch (err) {
      console.error('Error syncing external source:', err);
      setSyncingExternal(null);
    }
  };

  const getSourceIcon = (sourceType: string) => {
    switch (sourceType) {
      case 'sdoh': return <MapPin className="h-5 w-5 text-blue-500" />;
      case 'health': return <Activity className="h-5 w-5 text-red-500" />;
      case 'census': return <Users className="h-5 w-5 text-green-500" />;
      case 'cms': return <Shield className="h-5 w-5 text-purple-500" />;
      case 'trustcare': return <Database className="h-5 w-5 text-orange-500" />;
      case 'weavein': return <Brain className="h-5 w-5 text-indigo-500" />;
      default: return <Globe className="h-5 w-5 text-gray-500" />;
    }
  };

  const getExternalSourceIcon = (type: 'transportation' | 'climate' | 'housing') => {
    switch (type) {
      case 'transportation': return <Car className="h-5 w-5 text-blue-500" />;
      case 'climate': return <Cloud className="h-5 w-5 text-sky-500" />;
      case 'housing': return <Home className="h-5 w-5 text-amber-500" />;
    }
  };

  const getCategoryBadge = (category: string) => {
    switch (category) {
      case 'public':
        return <Badge className="bg-green-100 text-green-800 text-xs">Public</Badge>;
      case 'proprietary':
        return <Badge className="bg-orange-100 text-orange-800 text-xs">Proprietary</Badge>;
      case 'internal':
        return <Badge className="bg-blue-100 text-blue-800 text-xs">Internal</Badge>;
      default:
        return <Badge variant="outline" className="text-xs">{category}</Badge>;
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'Never';
    return fmt.medium(dateStr);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="flex gap-2 border-b">
        <button
          className={`px-4 py-2 font-medium text-sm transition-colors relative ${
            activeTab === 'ml-sources'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('ml-sources')}
        >
          <div className="flex items-center gap-2">
            <Database className="h-4 w-4" />
            ML Data Sources
          </div>
        </button>
        <button
          className={`px-4 py-2 font-medium text-sm transition-colors relative ${
            activeTab === 'external-sdoh'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('external-sdoh')}
        >
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            External SDOH Data
            <Badge className="bg-green-100 text-green-700 text-xs">New</Badge>
          </div>
        </button>
        <button
          className={`px-4 py-2 font-medium text-sm transition-colors relative ${
            activeTab === 'org-ml-privacy'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('org-ml-privacy')}
        >
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Org ML Privacy
            <Badge className="bg-purple-100 text-purple-700 text-xs">PHI</Badge>
          </div>
        </button>
        <button
          className={`px-4 py-2 font-medium text-sm transition-colors relative ${
            activeTab === 'pricing-data'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('pricing-data')}
        >
          <div className="flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            Pricing Data
            <Badge className="bg-emerald-100 text-emerald-700 text-xs">New</Badge>
          </div>
        </button>
      </div>

      {activeTab === 'ml-sources' && (
        <>
      {/* Header Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Database className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Data Sources</p>
              <p className="text-2xl font-bold">{stats?.active_sources || 0}/{stats?.total_sources || 0}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <MapPin className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">SDOH Records</p>
              <p className="text-2xl font-bold">{(stats?.total_sdoh_records || 0).toLocaleString()}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Users className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Training Members</p>
              <p className="text-2xl font-bold">{(stats?.total_training_members || 0).toLocaleString()}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Brain className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Active Model</p>
              <p className="text-lg font-bold truncate">{stats?.active_model || 'None'}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-3 flex-wrap">
        <Button onClick={handleOpenPredictionModal}>
          <Sparkles className="h-4 w-4 mr-2" />
          Generate Predictions
        </Button>
        <Button variant="outline" onClick={() => setShowSyncModal(true)}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Sync All Sources
        </Button>
        <Button variant="outline" onClick={() => setShowTrustCareModal(true)}>
          <Upload className="h-4 w-4 mr-2" />
          Import Trust.Care Data
        </Button>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Data Sources Grid */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">ML Data Sources</h3>
        <div className="space-y-4">
          {sources.map(source => (
            <div key={source.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
              {/* Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  {getSourceIcon(source.source_type)}
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{source.source_name}</span>
                      {getCategoryBadge(source.source_category)}
                      {source.is_active ? (
                        <Badge className="bg-green-100 text-green-800 text-xs">Active</Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-600 text-xs">Inactive</Badge>
                      )}
                      {source.is_validated && (
                        <Badge className="bg-blue-100 text-blue-800 text-xs">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Validated
                        </Badge>
                      )}
                    </div>
                    {source.description && (
                      <p className="text-sm text-gray-500 mt-1">{source.description}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {source.source_url && (
                    <a
                      href={source.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 hover:bg-gray-100 rounded"
                    >
                      <ExternalLink className="h-4 w-4 text-gray-500" />
                    </a>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleSyncSource(source)}
                    disabled={!source.is_active || syncing}
                  >
                    {syncing && selectedSource?.id === source.id ? (
                      <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                    ) : (
                      <RefreshCw className="h-3 w-3 mr-1" />
                    )}
                    Sync
                  </Button>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-sm">
                <div>
                  <span className="text-gray-500 block text-xs uppercase">Geography</span>
                  <span className="font-medium capitalize">{source.geography_level || '-'}</span>
                </div>
                <div>
                  <span className="text-gray-500 block text-xs uppercase">Domain</span>
                  <span className="font-medium capitalize">{source.data_domain || '-'}</span>
                </div>
                <div>
                  <span className="text-gray-500 block text-xs uppercase">Refresh</span>
                  <span className="font-medium capitalize">{source.refresh_frequency || '-'}</span>
                </div>
                <div>
                  <span className="text-gray-500 block text-xs uppercase">Records</span>
                  <span className="font-medium">{(source.record_count || 0).toLocaleString()}</span>
                </div>
                <div>
                  <span className="text-gray-500 block text-xs uppercase">Quality</span>
                  <span className={`font-medium ${
                    source.data_quality_score >= 80 ? 'text-green-600' :
                    source.data_quality_score >= 60 ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {source.data_quality_score || '-'}%
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 block text-xs uppercase">Last Updated</span>
                  <span className="font-medium">{formatDate(source.last_updated_date)}</span>
                </div>
              </div>

              {/* Sync Progress */}
              {syncing && selectedSource?.id === source.id && (
                <div className="mt-3 pt-3 border-t">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-600">Syncing...</span>
                    <span className="text-sm font-medium">{Math.round(syncProgress)}%</span>
                  </div>
                  <Progress value={syncProgress} className="h-2" />
                </div>
              )}

              {/* License Info */}
              {source.attribution_required && (
                <div className="mt-3 pt-3 border-t flex items-center gap-2 text-xs text-gray-500">
                  <FileText className="h-3 w-3" />
                  <span>License: {source.license_type || 'Public Domain'}</span>
                  <span>•</span>
                  <span>Attribution required</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>

      {/* Recent Sync Jobs */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Recent Sync Jobs</h3>
        {syncJobs.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No sync jobs yet. Start by syncing a data source above.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {syncJobs.map(job => (
              <div key={job.id} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-3">
                  <Badge className={
                    job.status === 'completed' ? 'bg-green-100 text-green-800' :
                    job.status === 'running' ? 'bg-blue-100 text-blue-800' :
                    job.status === 'failed' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800'
                  }>
                    {job.status}
                  </Badge>
                  <span className="font-medium">{job.source_system}</span>
                  <Badge variant="outline">{job.sync_mode}</Badge>
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <span>{job.records_loaded.toLocaleString()} records</span>
                  <span>{formatDate(job.started_at)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Trust.Care Import Modal */}
      <Dialog open={showTrustCareModal} onOpenChange={setShowTrustCareModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Import Trust.Care Historical Data</DialogTitle>
            <DialogDescription>
              Import de-identified member engagement data from Trust.Care for ML model training.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div>
                  <p className="font-medium text-yellow-800">Data De-identification Required</p>
                  <p className="text-sm text-yellow-700 mt-1">
                    Trust.Care data must be de-identified before import. Member IDs will be hashed,
                    dates of birth converted to age buckets, and PII fields removed.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="font-medium">Import Steps:</h4>
              <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600">
                <li>Export de-identified data from Trust.Care MySQL database</li>
                <li>Upload CSV files for: Members, SMS, Engagements, Conversations</li>
                <li>System will validate and transform to ML training schema</li>
                <li>Features will be computed and stored in the feature store</li>
              </ol>
            </div>

            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600">
                Drag and drop Trust.Care export files here, or click to browse
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Supported formats: CSV, JSON (max 100MB per file)
              </p>
              <Button variant="outline" size="sm" className="mt-3">
                Select Files
              </Button>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowTrustCareModal(false)}>
              Cancel
            </Button>
            <Button disabled>
              Start Import
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sync All Modal */}
      <Dialog open={showSyncModal} onOpenChange={setShowSyncModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sync All Data Sources</DialogTitle>
            <DialogDescription>
              This will refresh data from all active public SDOH sources.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <p className="text-sm text-gray-600 mb-4">
              The following sources will be synced:
            </p>
            <ul className="space-y-2">
              {sources.filter(s => s.is_active && s.source_category === 'public').map(source => (
                <li key={source.id} className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  {source.source_name}
                </li>
              ))}
            </ul>
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowSyncModal(false)}>
              Cancel
            </Button>
            <Button onClick={() => {
              setShowSyncModal(false);
              // Trigger sync for all sources
              sources.filter(s => s.is_active).forEach(source => {
                setTimeout(() => handleSyncSource(source), Math.random() * 1000);
              });
            }}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Start Sync
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Generate Predictions Modal */}
      <Dialog open={showPredictionModal} onOpenChange={setShowPredictionModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-500" />
              Generate ML Predictions
            </DialogTitle>
            <DialogDescription>
              Generate churn predictions for members based on engagement data and ML model analysis.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Organization Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Organization</label>
              <select
                className="w-full p-2 border rounded-lg bg-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                value={selectedOrgId || ''}
                onChange={(e) => setSelectedOrgId(Number(e.target.value))}
                disabled={generatingPredictions}
              >
                <option value="">Select an organization...</option>
                {organizations.map(org => (
                  <option key={org.id} value={org.id}>
                    {org.name} ({org.type})
                  </option>
                ))}
              </select>
            </div>

            {/* Prediction Horizon */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Prediction Horizon</label>
              <select
                className="w-full p-2 border rounded-lg bg-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                value={predictionHorizon}
                onChange={(e) => setPredictionHorizon(Number(e.target.value))}
                disabled={generatingPredictions}
              >
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
              </select>
              <p className="text-xs text-gray-500">
                Predict churn probability within the selected number of days
              </p>
            </div>

            {/* Info Box */}
            <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
              <div className="flex items-start gap-2">
                <Brain className="h-5 w-5 text-purple-600 mt-0.5" />
                <div className="text-sm text-purple-800">
                  <p className="font-medium">How it works</p>
                  <p className="text-purple-700 mt-1">
                    The ML model analyzes engagement scores, contact history, risk levels, and SDOH data
                    to predict which members are at risk of churning. Predictions are stored and can be
                    viewed in the Member Retention Dashboard.
                  </p>
                </div>
              </div>
            </div>

            {/* Result Display */}
            {predictionResult && (
              <div className={`p-4 rounded-lg ${
                predictionResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-start gap-2">
                  {predictionResult.success ? (
                    <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
                  )}
                  <div>
                    <p className={`font-medium ${predictionResult.success ? 'text-green-800' : 'text-red-800'}`}>
                      {predictionResult.message}
                    </p>
                    {predictionResult.success && predictionResult.predictions_created > 0 && (
                      <div className="mt-2 text-sm text-green-700">
                        <p><strong>{predictionResult.predictions_created}</strong> predictions generated</p>
                        {predictionResult.risk_distribution && (
                          <div className="mt-2 grid grid-cols-4 gap-2">
                            <div className="text-center p-2 bg-white rounded">
                              <p className="text-red-600 font-bold">{predictionResult.risk_distribution.critical}</p>
                              <p className="text-xs text-gray-500">Critical</p>
                            </div>
                            <div className="text-center p-2 bg-white rounded">
                              <p className="text-orange-600 font-bold">{predictionResult.risk_distribution.high}</p>
                              <p className="text-xs text-gray-500">High</p>
                            </div>
                            <div className="text-center p-2 bg-white rounded">
                              <p className="text-yellow-600 font-bold">{predictionResult.risk_distribution.moderate}</p>
                              <p className="text-xs text-gray-500">Moderate</p>
                            </div>
                            <div className="text-center p-2 bg-white rounded">
                              <p className="text-green-600 font-bold">{predictionResult.risk_distribution.low}</p>
                              <p className="text-xs text-gray-500">Low</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowPredictionModal(false)}>
              {predictionResult?.success ? 'Close' : 'Cancel'}
            </Button>
            {!predictionResult?.success && (
              <Button
                onClick={handleGeneratePredictions}
                disabled={!selectedOrgId || generatingPredictions}
              >
                {generatingPredictions ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate Predictions
                  </>
                )}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
        </>
      )}

      {/* External SDOH Data Tab */}
      {activeTab === 'external-sdoh' && (
        <>
          {/* External SDOH Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Car className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Transportation</p>
                  <p className="text-2xl font-bold">{externalSources.find(s => s.type === 'transportation')?.record_count.toLocaleString() || 0}</p>
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-sky-100 rounded-lg">
                  <Cloud className="h-5 w-5 text-sky-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Climate Risk</p>
                  <p className="text-2xl font-bold">{externalSources.find(s => s.type === 'climate')?.record_count.toLocaleString() || 0}</p>
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Home className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Housing Risk</p>
                  <p className="text-2xl font-bold">{externalSources.find(s => s.type === 'housing')?.record_count.toLocaleString() || 0}</p>
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <MapPin className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">ZIP Codes</p>
                  <p className="text-2xl font-bold">41,000+</p>
                </div>
              </div>
            </Card>
          </div>

          {/* ZIP Code Lookup */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <MapPin className="h-5 w-5 text-gray-500" />
              ZIP Code SDOH Lookup
            </h3>
            <div className="flex gap-3 mb-4">
              <input
                type="text"
                placeholder="Enter ZIP code (e.g., 90210)"
                className="flex-1 p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={zipCodeLookup}
                onChange={(e) => setZipCodeLookup(e.target.value.replace(/\D/g, '').slice(0, 5))}
                maxLength={5}
              />
              <Button onClick={handleLookupZipCode} disabled={lookingUpZip || zipCodeLookup.length < 5}>
                {lookingUpZip ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <MapPin className="h-4 w-4 mr-2" />
                )}
                Lookup
              </Button>
            </div>

            {zipCodeData && (
              <div className="border rounded-lg p-4 bg-gray-50">
                <h4 className="font-semibold mb-3">SDOH Data for ZIP {zipCodeData.zip_code}</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Transportation */}
                  <div className="bg-white p-4 rounded-lg border">
                    <div className="flex items-center gap-2 mb-3">
                      <Car className="h-5 w-5 text-blue-500" />
                      <span className="font-medium">Transportation</span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Walk Score</span>
                        <span className="font-medium">{zipCodeData.transportation?.walk_score ?? 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Transit Score</span>
                        <span className="font-medium">{zipCodeData.transportation?.transit_score ?? 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Bike Score</span>
                        <span className="font-medium">{zipCodeData.transportation?.bike_score ?? 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Barrier Score</span>
                        <span className={`font-medium ${
                          (zipCodeData.transportation?.transportation_barrier_score ?? 0) > 70 ? 'text-red-600' :
                          (zipCodeData.transportation?.transportation_barrier_score ?? 0) > 40 ? 'text-yellow-600' : 'text-green-600'
                        }`}>
                          {zipCodeData.transportation?.transportation_barrier_score ?? 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Climate */}
                  <div className="bg-white p-4 rounded-lg border">
                    <div className="flex items-center gap-2 mb-3">
                      <Cloud className="h-5 w-5 text-sky-500" />
                      <span className="font-medium">Climate & Environment</span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Climate Risk</span>
                        <span className={`font-medium ${
                          (zipCodeData.climate?.climate_risk_score ?? 0) > 70 ? 'text-red-600' :
                          (zipCodeData.climate?.climate_risk_score ?? 0) > 40 ? 'text-yellow-600' : 'text-green-600'
                        }`}>
                          {zipCodeData.climate?.climate_risk_score ?? 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Air Quality (AQI)</span>
                        <span className="font-medium">{zipCodeData.climate?.aqi_annual_median ?? 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Flood Risk</span>
                        <Badge className={
                          zipCodeData.climate?.flood_risk_level === 'high' ? 'bg-red-100 text-red-800' :
                          zipCodeData.climate?.flood_risk_level === 'moderate' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }>
                          {zipCodeData.climate?.flood_risk_level ?? 'N/A'}
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Wildfire Risk</span>
                        <Badge className={
                          zipCodeData.climate?.wildfire_risk_level === 'extreme' || zipCodeData.climate?.wildfire_risk_level === 'very_high' ? 'bg-red-100 text-red-800' :
                          zipCodeData.climate?.wildfire_risk_level === 'high' || zipCodeData.climate?.wildfire_risk_level === 'moderate' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }>
                          {zipCodeData.climate?.wildfire_risk_level ?? 'N/A'}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  {/* Housing */}
                  <div className="bg-white p-4 rounded-lg border">
                    <div className="flex items-center gap-2 mb-3">
                      <Home className="h-5 w-5 text-amber-500" />
                      <span className="font-medium">Housing</span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Housing Risk</span>
                        <span className={`font-medium ${
                          (zipCodeData.housing?.housing_risk_score ?? 0) > 70 ? 'text-red-600' :
                          (zipCodeData.housing?.housing_risk_score ?? 0) > 40 ? 'text-yellow-600' : 'text-green-600'
                        }`}>
                          {zipCodeData.housing?.housing_risk_score ?? 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Eviction Rate</span>
                        <span className="font-medium">{zipCodeData.housing?.eviction_rate ?? 'N/A'}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Cost Burdened</span>
                        <span className="font-medium">{zipCodeData.housing?.pct_cost_burdened ?? 'N/A'}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Stability Score</span>
                        <span className={`font-medium ${
                          (zipCodeData.housing?.housing_stability_score ?? 0) > 70 ? 'text-green-600' :
                          (zipCodeData.housing?.housing_stability_score ?? 0) > 40 ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {zipCodeData.housing?.housing_stability_score ?? 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Combined Risk Score */}
                {zipCodeData.combined_sdoh_risk_score && (
                  <div className="mt-4 p-3 bg-white rounded-lg border flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-purple-500" />
                      <span className="font-medium">Combined SDOH Risk Score</span>
                    </div>
                    <span className={`text-xl font-bold ${
                      zipCodeData.combined_sdoh_risk_score > 70 ? 'text-red-600' :
                      zipCodeData.combined_sdoh_risk_score > 40 ? 'text-yellow-600' : 'text-green-600'
                    }`}>
                      {zipCodeData.combined_sdoh_risk_score}
                    </span>
                  </div>
                )}
              </div>
            )}
          </Card>

          {/* External SDOH Sources */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Globe className="h-5 w-5 text-gray-500" />
              External SDOH Data Sources
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Integration with external APIs to enrich member SDOH profiles with transportation, climate, and housing risk data.
            </p>

            <div className="space-y-4">
              {externalSources.map(source => (
                <div key={source.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      {getExternalSourceIcon(source.type)}
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{source.name}</span>
                          <Badge className={
                            source.api_status === 'connected' ? 'bg-green-100 text-green-800' :
                            source.api_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }>
                            {source.api_status}
                          </Badge>
                          {source.is_active && (
                            <Badge className="bg-blue-100 text-blue-800">Active</Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">{source.description}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSyncExternalSource(source.id)}
                        disabled={syncingExternal === source.id || source.api_status === 'error'}
                      >
                        {syncingExternal === source.id ? (
                          <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                        ) : (
                          <RefreshCw className="h-3 w-3 mr-1" />
                        )}
                        Sync
                      </Button>
                    </div>
                  </div>

                  {/* Data Fields */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    {source.data_fields.map((field, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">
                        {field}
                      </Badge>
                    ))}
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500 block text-xs uppercase">API Provider</span>
                      <span className="font-medium">{source.api_provider}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block text-xs uppercase">Records</span>
                      <span className="font-medium">{source.record_count.toLocaleString()}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block text-xs uppercase">Last Sync</span>
                      <span className="font-medium">{source.last_sync ? formatDate(source.last_sync) : 'Never'}</span>
                    </div>
                  </div>

                  {/* Sync Progress */}
                  {syncingExternal === source.id && (
                    <div className="mt-3 pt-3 border-t">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-gray-600">Syncing from {source.api_provider}...</span>
                        <span className="text-sm font-medium">{Math.round(externalSyncProgress)}%</span>
                      </div>
                      <Progress value={externalSyncProgress} className="h-2" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>

          {/* API Integration Info */}
          <Card className="p-6 bg-blue-50 border-blue-200">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Settings className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-blue-900">External API Integration</h4>
                <p className="text-sm text-blue-800 mt-1">
                  These data sources integrate with external APIs to fetch real-time SDOH data for member ZIP codes.
                  Data is cached for 30 days to optimize API usage and costs.
                </p>
                <div className="mt-3 flex flex-wrap gap-4 text-sm text-blue-700">
                  <div className="flex items-center gap-1">
                    <Car className="h-4 w-4" />
                    <span>Walk Score API</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Thermometer className="h-4 w-4" />
                    <span>EPA AQI API</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Droplets className="h-4 w-4" />
                    <span>FEMA Flood Maps</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Flame className="h-4 w-4" />
                    <span>USGS Wildfire Data</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Home className="h-4 w-4" />
                    <span>Eviction Lab</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Building2 className="h-4 w-4" />
                    <span>HUD Housing Data</span>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </>
      )}

      {/* Organization ML Privacy Tab */}
      {activeTab === 'org-ml-privacy' && (
        <>
          {/* Privacy Overview */}
          <Card className="p-6 bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-purple-100 rounded-xl">
                <Shield className="h-6 w-6 text-purple-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-purple-900">Organization ML Data Privacy</h3>
                <p className="text-sm text-purple-700 mt-1">
                  Configure how member data is used for ML model training. Organizations can choose between
                  organization-only ML models (using PII/PHI data) and de-identified cross-organization models.
                </p>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white/80 p-4 rounded-lg border border-purple-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="h-5 w-5 text-blue-600" />
                      <span className="font-medium text-blue-900">Organization-Specific ML</span>
                    </div>
                    <p className="text-xs text-gray-600">
                      Uses full PII/PHI data for ML predictions. Models are trained exclusively on
                      the organization's own member data. Highest accuracy for the specific population.
                    </p>
                  </div>
                  <div className="bg-white/80 p-4 rounded-lg border border-purple-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Globe className="h-5 w-5 text-green-600" />
                      <span className="font-medium text-green-900">De-identified Cross-Org ML</span>
                    </div>
                    <p className="text-xs text-gray-600">
                      Uses de-identified, aggregated data across organizations. Improves model performance
                      through larger training sets while maintaining privacy compliance.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Two-Tier Model Architecture */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Brain className="h-5 w-5 text-gray-500" />
              Two-Tier ML Model Architecture
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Tier 1: Organization-Specific */}
              <div className="border-2 border-blue-200 rounded-xl p-5 bg-blue-50/30">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Users className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-900">Tier 1: Organization-Specific</h4>
                    <Badge className="bg-blue-100 text-blue-700">PII/PHI Enabled</Badge>
                  </div>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-blue-500 mt-0.5" />
                    <span>Full member PII/PHI for personalized predictions</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-blue-500 mt-0.5" />
                    <span>Organization-specific feature engineering</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-blue-500 mt-0.5" />
                    <span>Real-time prediction with member context</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-blue-500 mt-0.5" />
                    <span>HIPAA-compliant data isolation</span>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-white rounded-lg border">
                  <div className="text-xs text-gray-500 uppercase mb-1">Data Used</div>
                  <div className="flex flex-wrap gap-1.5">
                    <Badge variant="outline" className="text-xs">Name</Badge>
                    <Badge variant="outline" className="text-xs">DOB</Badge>
                    <Badge variant="outline" className="text-xs">Address</Badge>
                    <Badge variant="outline" className="text-xs">Health Conditions</Badge>
                    <Badge variant="outline" className="text-xs">Engagement History</Badge>
                    <Badge variant="outline" className="text-xs">Care Plans</Badge>
                  </div>
                </div>
              </div>

              {/* Tier 2: De-identified Cross-Org */}
              <div className="border-2 border-green-200 rounded-xl p-5 bg-green-50/30">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Globe className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-green-900">Tier 2: De-identified Cross-Org</h4>
                    <Badge className="bg-green-100 text-green-700">Privacy-Preserving</Badge>
                  </div>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5" />
                    <span>Aggregated, de-identified data across orgs</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5" />
                    <span>Larger training dataset for better generalization</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5" />
                    <span>Benchmarking across similar populations</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5" />
                    <span>Federated learning compatible</span>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-white rounded-lg border">
                  <div className="text-xs text-gray-500 uppercase mb-1">Data Used (De-identified)</div>
                  <div className="flex flex-wrap gap-1.5">
                    <Badge variant="outline" className="text-xs">Age Range</Badge>
                    <Badge variant="outline" className="text-xs">ZIP (3-digit)</Badge>
                    <Badge variant="outline" className="text-xs">Risk Category</Badge>
                    <Badge variant="outline" className="text-xs">SDOH Scores</Badge>
                    <Badge variant="outline" className="text-xs">Engagement Patterns</Badge>
                    <Badge variant="outline" className="text-xs">Outcome Metrics</Badge>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Organization Configuration */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Settings className="h-5 w-5 text-gray-500" />
                  Organization ML Privacy Configuration
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Configure ML data sharing settings for each organization. Changes take effect on the next model training cycle.
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={fetchOrgMLConfigs} disabled={loadingOrgConfigs}>
                <RefreshCw className={`h-4 w-4 mr-2 ${loadingOrgConfigs ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>

            {/* Error Message */}
            {orgConfigError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm">{orgConfigError}</span>
              </div>
            )}

            {/* Loading State */}
            {loadingOrgConfigs ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
              </div>
            ) : orgMLConfigs.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Building2 className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p className="font-medium">No organizations configured</p>
                <p className="text-sm mt-1">Organization ML privacy settings will appear here once organizations are set up.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Organization</th>
                      <th className="px-4 py-3 text-center font-medium text-gray-600">Org-Specific ML</th>
                      <th className="px-4 py-3 text-center font-medium text-gray-600">Cross-Org ML</th>
                      <th className="px-4 py-3 text-center font-medium text-gray-600">Members</th>
                      <th className="px-4 py-3 text-center font-medium text-gray-600">Last Training</th>
                      <th className="px-4 py-3 text-center font-medium text-gray-600">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {orgMLConfigs.map((config) => (
                      <tr key={config.org_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-gray-400" />
                            <span className="font-medium">{config.org_name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => handleTogglePiiML(config.org_id, config.pii_ml_enabled)}
                            disabled={savingOrgConfig === config.org_id}
                            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer
                              ${config.pii_ml_enabled
                                ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }
                              ${savingOrgConfig === config.org_id ? 'opacity-50' : ''}
                            `}
                          >
                            {savingOrgConfig === config.org_id ? (
                              <RefreshCw className="h-3 w-3 animate-spin" />
                            ) : null}
                            {config.pii_ml_enabled ? 'Enabled' : 'Disabled'}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => handleToggleCrossOrgML(config.org_id, config.deidentified_ml_enabled)}
                            disabled={savingOrgConfig === config.org_id}
                            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer
                              ${config.deidentified_ml_enabled
                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }
                              ${savingOrgConfig === config.org_id ? 'opacity-50' : ''}
                            `}
                          >
                            {savingOrgConfig === config.org_id ? (
                              <RefreshCw className="h-3 w-3 animate-spin" />
                            ) : null}
                            {config.deidentified_ml_enabled ? 'Opted-In' : 'Opted-Out'}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-center font-medium">
                          {config.member_count.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-center text-gray-500">
                          {config.last_training_date
                            ? fmt.medium(config.last_training_date)
                            : 'Never'
                          }
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Badge className={
                            config.status === 'Active' ? 'bg-green-100 text-green-700' :
                            config.status === 'Limited' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-600'
                          }>
                            {config.status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* Privacy Compliance Info */}
          <Card className="p-6 bg-amber-50 border-amber-200">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <AlertCircle className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <h4 className="font-semibold text-amber-900">Privacy & Compliance Considerations</h4>
                <div className="text-sm text-amber-800 mt-2 space-y-2">
                  <p>
                    <strong>HIPAA Compliance:</strong> Organization-specific ML models with PII/PHI data are stored
                    in isolated, encrypted databases with audit logging. Access is restricted to authorized personnel only.
                  </p>
                  <p>
                    <strong>De-identification Standards:</strong> Cross-organization data follows Safe Harbor
                    de-identification standards (45 CFR 164.514(b)(2)), removing all 18 HIPAA identifiers.
                  </p>
                  <p>
                    <strong>Data Retention:</strong> Training data is retained according to organizational policies.
                    De-identified data is aggregated and cannot be re-identified.
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </>
      )}

      {/* Pricing Data Sources Tab */}
      {activeTab === 'pricing-data' && (
        <>
          {/* Pricing Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <Receipt className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Codes</p>
                  <p className="text-2xl font-bold">{pricingStats.total_codes.toLocaleString()}</p>
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Shield className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Medicare Codes</p>
                  <p className="text-2xl font-bold">{pricingStats.medicare_codes}</p>
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Building2 className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">States Covered</p>
                  <p className="text-2xl font-bold">{pricingStats.states_covered}</p>
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Brain className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">ML Accuracy</p>
                  <p className="text-2xl font-bold">{(pricingStats.ml_model_accuracy * 100).toFixed(0)}%</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3 flex-wrap">
            <Button
              onClick={async () => {
                setRefreshingPricing(true);
                // Simulate full refresh
                await new Promise(resolve => setTimeout(resolve, 2000));
                setPricingStats(prev => ({
                  ...prev,
                  last_full_refresh: new Date().toISOString()
                }));
                setRefreshingPricing(false);
              }}
              disabled={refreshingPricing}
            >
              {refreshingPricing ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Refresh All Pricing Data
            </Button>
            <Button variant="outline">
              <Download className="h-4 w-4 mr-2" />
              Export Rate Schedule
            </Button>
          </div>

          {/* Code Lookup */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Search className="h-5 w-5 text-gray-500" />
              Billing Code Pricing Lookup
            </h3>
            <div className="flex gap-3 mb-4">
              <input
                type="text"
                placeholder="Enter CPT/HCPCS code (e.g., 99490, G0019)"
                className="flex-1 p-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                value={pricingCodeLookup}
                onChange={(e) => setPricingCodeLookup(e.target.value.toUpperCase())}
              />
              <Button
                onClick={async () => {
                  if (!pricingCodeLookup) return;
                  setLookingUpPricing(true);
                  // Simulate API call
                  await new Promise(resolve => setTimeout(resolve, 800));
                  setPricingLookupResult(getSamplePricingData(pricingCodeLookup));
                  setLookingUpPricing(false);
                }}
                disabled={lookingUpPricing || !pricingCodeLookup}
              >
                {lookingUpPricing ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Calculator className="h-4 w-4 mr-2" />
                )}
                Get Pricing
              </Button>
            </div>

            {pricingLookupResult && (
              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="font-semibold text-lg">{pricingLookupResult.billing_code}</h4>
                    <p className="text-gray-600">{pricingLookupResult.description}</p>
                  </div>
                  <Badge className={
                    pricingLookupResult.confidence >= 0.8 ? 'bg-green-100 text-green-700' :
                    pricingLookupResult.confidence >= 0.6 ? 'bg-yellow-100 text-yellow-700' :
                    'bg-gray-100 text-gray-600'
                  }>
                    {(pricingLookupResult.confidence * 100).toFixed(0)}% confidence
                  </Badge>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Recommended Rate */}
                  <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                    <div className="flex items-center gap-2 mb-2">
                      <DollarSign className="h-5 w-5 text-emerald-600" />
                      <span className="font-medium text-emerald-900">ML Recommended Rate</span>
                    </div>
                    <p className="text-2xl font-bold text-emerald-700">
                      ${pricingLookupResult.ml_recommended_rate?.toFixed(2) || 'N/A'}
                    </p>
                    <p className="text-xs text-emerald-600 mt-1">
                      Range: ${pricingLookupResult.rate_range?.low?.toFixed(2)} - ${pricingLookupResult.rate_range?.high?.toFixed(2)}
                    </p>
                  </div>

                  {/* Source Rates */}
                  <div className="bg-white p-4 rounded-lg border">
                    <div className="flex items-center gap-2 mb-2">
                      <Layers className="h-5 w-5 text-gray-500" />
                      <span className="font-medium">Source Rates</span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Medicare (CMS)</span>
                        <span className="font-medium">${pricingLookupResult.source_rates?.medicare?.toFixed(2) || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Medicaid (CA)</span>
                        <span className="font-medium">${pricingLookupResult.source_rates?.medicaid?.toFixed(2) || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Commercial Est.</span>
                        <span className="font-medium">${pricingLookupResult.source_rates?.commercial?.toFixed(2) || 'N/A'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Market Analysis */}
                  <div className="bg-white p-4 rounded-lg border">
                    <div className="flex items-center gap-2 mb-2">
                      <BarChart3 className="h-5 w-5 text-gray-500" />
                      <span className="font-medium">Market Analysis</span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Market Average</span>
                        <span className="font-medium">${pricingLookupResult.market_analysis?.average?.toFixed(2) || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Market Median</span>
                        <span className="font-medium">${pricingLookupResult.market_analysis?.median?.toFixed(2) || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Data Points</span>
                        <span className="font-medium">{pricingLookupResult.data_points || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ML Factors */}
                {pricingLookupResult.ml_factors && (
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Brain className="h-4 w-4 text-blue-600" />
                      <span className="text-sm font-medium text-blue-900">ML Model Factors</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(pricingLookupResult.ml_factors).map(([key, value]) => (
                        <Badge key={key} variant="outline" className="text-xs">
                          {key}: {typeof value === 'number' ? value.toFixed(2) : String(value)}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </Card>

          {/* Pricing Data Sources */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Database className="h-5 w-5 text-gray-500" />
              Pricing Data Sources
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Integrated data sources for service pricing comparatives, rate benchmarks, and ML model training.
            </p>

            <div className="space-y-4">
              {pricingSources.map(source => (
                <div key={source.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      {source.source_type === 'cms_mpfs' ? (
                        <Shield className="h-5 w-5 text-blue-500" />
                      ) : source.source_type === 'medicaid' ? (
                        <Building2 className="h-5 w-5 text-purple-500" />
                      ) : source.source_type === 'commercial' ? (
                        <TrendingUp className="h-5 w-5 text-green-500" />
                      ) : (
                        <Database className="h-5 w-5 text-gray-500" />
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{source.name}</span>
                          <Badge className={
                            source.status === 'connected' ? 'bg-green-100 text-green-800' :
                            source.status === 'syncing' ? 'bg-blue-100 text-blue-800' :
                            source.status === 'error' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-600'
                          }>
                            {source.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">{source.description}</p>
                      </div>
                    </div>

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        setSyncingPricingSource(source.id);
                        await new Promise(resolve => setTimeout(resolve, 1500));
                        setPricingSources(prev => prev.map(s =>
                          s.id === source.id
                            ? { ...s, last_sync: new Date().toISOString(), status: 'connected' as const }
                            : s
                        ));
                        setSyncingPricingSource(null);
                      }}
                      disabled={syncingPricingSource === source.id}
                    >
                      {syncingPricingSource === source.id ? (
                        <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                      ) : (
                        <RefreshCw className="h-3 w-3 mr-1" />
                      )}
                      Sync
                    </Button>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500 block text-xs uppercase">Codes</span>
                      <span className="font-medium">{source.codes_covered}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block text-xs uppercase">Records</span>
                      <span className="font-medium">{source.record_count.toLocaleString()}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block text-xs uppercase">States</span>
                      <span className="font-medium">{source.states_covered.join(', ')}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block text-xs uppercase">Quality</span>
                      <span className={`font-medium ${
                        source.data_quality_score >= 80 ? 'text-green-600' :
                        source.data_quality_score >= 60 ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {source.data_quality_score}%
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 block text-xs uppercase">Last Sync</span>
                      <span className="font-medium">
                        {source.last_sync ? formatDate(source.last_sync) : 'Never'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* ML Pricing Model Info */}
          <Card className="p-6 bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-emerald-100 rounded-xl">
                <Brain className="h-6 w-6 text-emerald-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-emerald-900">ML Pricing Recommendation Engine</h3>
                <p className="text-sm text-emerald-700 mt-1">
                  The pricing ML model uses gradient boosting to predict optimal service rates based on:
                </p>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white/80 p-4 rounded-lg border border-emerald-200">
                    <h4 className="font-medium text-emerald-900 mb-2">Input Features</h4>
                    <ul className="text-sm text-emerald-700 space-y-1">
                      <li>• CMS Medicare Fee Schedule (MPFS)</li>
                      <li>• Medicaid State Fee Schedules</li>
                      <li>• Geographic Practice Cost Index (GPCI)</li>
                      <li>• Historical Accepted Rates</li>
                      <li>• Provider Quality Scores</li>
                    </ul>
                  </div>
                  <div className="bg-white/80 p-4 rounded-lg border border-emerald-200">
                    <h4 className="font-medium text-emerald-900 mb-2">Model Performance</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between text-emerald-700">
                        <span>Model Version</span>
                        <span className="font-medium">{pricingStats.ml_model_version}</span>
                      </div>
                      <div className="flex justify-between text-emerald-700">
                        <span>R² Score</span>
                        <span className="font-medium">{(pricingStats.ml_model_accuracy * 100).toFixed(1)}%</span>
                      </div>
                      <div className="flex justify-between text-emerald-700">
                        <span>Last Trained</span>
                        <span className="font-medium">{formatDate(pricingStats.last_full_refresh)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </>
      )}
    </div>
  );
};

// Mock data for initial development
function getMockSources(): MLDataSource[] {
  return [
    {
      id: 1,
      source_name: 'AHRQ SDOH Database',
      source_type: 'sdoh',
      source_category: 'public',
      source_url: 'https://www.ahrq.gov/sdoh/data-analytics/sdoh-data.html',
      geography_level: 'county',
      data_domain: 'social',
      refresh_frequency: 'annual',
      last_updated_date: '2024-03-15',
      is_active: true,
      is_validated: true,
      data_quality_score: 92,
      record_count: 3143,
      coverage_percentage: 100,
      description: 'Social determinants of health data at county/ZIP/tract levels',
      license_type: 'Public Domain',
      attribution_required: true
    },
    {
      id: 2,
      source_name: 'CDC PLACES',
      source_type: 'health',
      source_category: 'public',
      source_url: 'https://www.cdc.gov/places/',
      geography_level: 'county',
      data_domain: 'healthcare',
      refresh_frequency: 'annual',
      last_updated_date: '2024-01-10',
      is_active: true,
      is_validated: true,
      data_quality_score: 88,
      record_count: 3143,
      coverage_percentage: 100,
      description: 'Chronic disease prevalence and health behaviors',
      license_type: 'Public Domain',
      attribution_required: true
    },
    {
      id: 3,
      source_name: 'Census ACS',
      source_type: 'census',
      source_category: 'public',
      source_url: 'https://data.census.gov/',
      geography_level: 'tract',
      data_domain: 'social',
      refresh_frequency: 'annual',
      last_updated_date: '2024-02-20',
      is_active: true,
      is_validated: true,
      data_quality_score: 95,
      record_count: 84414,
      coverage_percentage: 100,
      description: 'American Community Survey demographic and economic data',
      license_type: 'Public Domain',
      attribution_required: false
    },
    {
      id: 4,
      source_name: 'CMS Medicaid Data',
      source_type: 'cms',
      source_category: 'public',
      source_url: 'https://data.cms.gov/',
      geography_level: 'state',
      data_domain: 'healthcare',
      refresh_frequency: 'quarterly',
      last_updated_date: '2024-06-01',
      is_active: true,
      is_validated: false,
      data_quality_score: 85,
      record_count: 51,
      coverage_percentage: 100,
      description: 'Medicaid enrollment and eligibility data',
      license_type: 'Public Domain',
      attribution_required: true
    },
    {
      id: 5,
      source_name: 'Trust.Care Historical',
      source_type: 'trustcare',
      source_category: 'proprietary',
      source_url: '',
      geography_level: 'member',
      data_domain: 'behavioral',
      refresh_frequency: 'one-time',
      last_updated_date: '',
      is_active: false,
      is_validated: false,
      data_quality_score: 0,
      record_count: 0,
      coverage_percentage: 0,
      description: 'Historical member engagement data from Trust.Care (pending import)',
      license_type: 'Proprietary',
      attribution_required: false
    },
    {
      id: 6,
      source_name: 'WeaveIn Live Data',
      source_type: 'weavein',
      source_category: 'internal',
      source_url: '',
      geography_level: 'member',
      data_domain: 'behavioral',
      refresh_frequency: 'daily',
      last_updated_date: new Date().toISOString(),
      is_active: true,
      is_validated: true,
      data_quality_score: 98,
      record_count: 15420,
      coverage_percentage: 100,
      description: 'Live engagement data from WeaveIn.ai platform',
      license_type: 'Internal',
      attribution_required: false
    }
  ];
}

function getMockStats(): MLStats {
  return {
    total_sources: 7,
    active_sources: 5,
    total_sdoh_records: 90700,
    total_training_members: 15420,
    total_features_computed: 924120,
    last_feature_refresh: new Date().toISOString(),
    model_count: 3,
    active_model: 'churn_v1.2'
  };
}

// External SDOH data sources configuration
function getExternalSDOHSources(): ExternalSDOHSource[] {
  return [
    {
      id: 'transportation',
      name: 'Transportation Accessibility',
      type: 'transportation',
      api_provider: 'Walk Score + Census',
      description: 'Walk Score, Transit Score, Bike Score, and Census commute data for ZIP codes',
      data_fields: ['Walk Score', 'Transit Score', 'Bike Score', 'Commute Time', 'Vehicle Access', 'Public Transit %'],
      record_count: 41702,
      is_active: true,
      api_status: 'connected'
    },
    {
      id: 'climate',
      name: 'Climate & Environmental Risk',
      type: 'climate',
      api_provider: 'EPA + FEMA + USGS',
      description: 'Air quality index, flood risk zones, wildfire risk, and severe weather data',
      data_fields: ['AQI', 'Flood Zone', 'Wildfire Risk', 'Tornado Risk', 'Hurricane Risk', 'Superfund Sites'],
      record_count: 41702,
      is_active: true,
      api_status: 'connected'
    },
    {
      id: 'housing',
      name: 'Housing Risk & Stability',
      type: 'housing',
      api_provider: 'Eviction Lab + HUD + Census',
      description: 'Eviction rates, housing cost burden, affordability, and housing quality indicators',
      data_fields: ['Eviction Rate', 'Cost Burden %', 'Median Rent', 'Vacancy Rate', 'Homelessness', 'Housing Quality'],
      record_count: 41702,
      is_active: true,
      api_status: 'connected'
    }
  ];
}

// Pricing data sources configuration
function getDefaultPricingSources() {
  return [
    {
      id: 'cms_mpfs',
      name: 'CMS Medicare Physician Fee Schedule',
      source_type: 'cms_mpfs' as const,
      description: 'Official Medicare rates with RVUs, GPCI adjustments, and conversion factors',
      status: 'connected' as const,
      last_sync: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
      record_count: 16500,
      codes_covered: 45,
      states_covered: ['CA', 'NY', 'TX', 'FL', 'IL'],
      data_quality_score: 98
    },
    {
      id: 'medicaid_ca',
      name: 'California Medi-Cal Fee Schedule',
      source_type: 'medicaid' as const,
      description: 'Medi-Cal reimbursement rates for community health services',
      status: 'connected' as const,
      last_sync: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
      record_count: 8200,
      codes_covered: 38,
      states_covered: ['CA'],
      data_quality_score: 92
    },
    {
      id: 'commercial_fair_health',
      name: 'Commercial Rate Estimates',
      source_type: 'commercial' as const,
      description: 'Estimated commercial rates based on Medicare multipliers and market data',
      status: 'connected' as const,
      last_sync: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
      record_count: 12000,
      codes_covered: 45,
      states_covered: ['CA', 'NY', 'TX', 'FL'],
      data_quality_score: 75
    },
    {
      id: 'internal_historical',
      name: 'Internal Historical Rates',
      source_type: 'internal' as const,
      description: 'Historical accepted rates from finalized service referrals',
      status: 'connected' as const,
      last_sync: new Date().toISOString(),
      record_count: 2450,
      codes_covered: 28,
      states_covered: ['CA'],
      data_quality_score: 95
    }
  ];
}

function getDefaultPricingStats() {
  return {
    total_codes: 45,
    medicare_codes: 45,
    medicaid_codes: 38,
    states_covered: 5,
    last_full_refresh: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    ml_model_version: 'pricing_v1.0',
    ml_model_accuracy: 0.87
  };
}

// Sample pricing data for code lookup
function getSamplePricingData(code: string) {
  const pricingDatabase: Record<string, any> = {
    '99490': {
      billing_code: '99490',
      description: 'Chronic Care Management - first 20 minutes per calendar month',
      ml_recommended_rate: 52.50,
      rate_range: { low: 42.17, high: 64.69 },
      source_rates: { medicare: 51.74, medicaid: 42.17, commercial: 64.69 },
      market_analysis: { average: 52.87, median: 51.74 },
      confidence: 0.92,
      data_points: 4,
      ml_factors: { cost_of_living: 1.12, service_complexity: 1.0, market_position: 0.95 }
    },
    '99491': {
      billing_code: '99491',
      description: 'Chronic Care Management - first 30 minutes per calendar month',
      ml_recommended_rate: 96.20,
      rate_range: { low: 83.64, high: 118.00 },
      source_rates: { medicare: 94.41, medicaid: 83.64, commercial: 118.00 },
      market_analysis: { average: 98.68, median: 94.41 },
      confidence: 0.89,
      data_points: 4,
      ml_factors: { cost_of_living: 1.12, service_complexity: 1.05, market_position: 0.95 }
    },
    'G0019': {
      billing_code: 'G0019',
      description: 'Community Health Integration - per calendar month',
      ml_recommended_rate: 105.80,
      rate_range: { low: 95.50, high: 139.16 },
      source_rates: { medicare: 111.33, medicaid: 95.50, commercial: 139.16 },
      market_analysis: { average: 115.33, median: 111.33 },
      confidence: 0.88,
      data_points: 4,
      ml_factors: { cost_of_living: 1.12, service_complexity: 1.02, market_position: 0.95 }
    },
    '90837': {
      billing_code: '90837',
      description: 'Psychotherapy, 53+ minutes',
      ml_recommended_rate: 158.50,
      rate_range: { low: 137.00, high: 193.44 },
      source_rates: { medicare: 154.75, medicaid: 137.00, commercial: 193.44 },
      market_analysis: { average: 161.73, median: 154.75 },
      confidence: 0.91,
      data_points: 4,
      ml_factors: { cost_of_living: 1.12, service_complexity: 1.08, market_position: 0.95 }
    },
    'H0044': {
      billing_code: 'H0044',
      description: 'Supported Housing Services - per diem',
      ml_recommended_rate: 46.20,
      rate_range: { low: 35.00, high: 58.00 },
      source_rates: { medicare: 50.30, medicaid: 48.50, commercial: 62.88 },
      market_analysis: { average: 53.89, median: 50.30 },
      confidence: 0.85,
      data_points: 4,
      ml_factors: { cost_of_living: 1.12, service_complexity: 0.85, market_position: 0.95 }
    },
    'T2003': {
      billing_code: 'T2003',
      description: 'Non-Emergency Transportation - per trip',
      ml_recommended_rate: 35.50,
      rate_range: { low: 32.00, high: 48.00 },
      source_rates: { medicare: null, medicaid: 32.00, commercial: 48.00 },
      market_analysis: { average: 40.00, median: 38.00 },
      confidence: 0.78,
      data_points: 2,
      ml_factors: { cost_of_living: 1.12, service_complexity: 0.70, market_position: 0.95 }
    }
  };

  // Return data for known codes, or generate placeholder for unknown codes
  if (pricingDatabase[code]) {
    return pricingDatabase[code];
  }

  // Generate placeholder data for unknown codes
  return {
    billing_code: code,
    description: `Service code ${code} - Rate data pending`,
    ml_recommended_rate: null,
    rate_range: { low: null, high: null },
    source_rates: { medicare: null, medicaid: null, commercial: null },
    market_analysis: { average: null, median: null },
    confidence: 0.0,
    data_points: 0,
    ml_factors: {}
  };
}

export default MLDataSourcesTab;
